import { Download, Trash2 } from "lucide-react";
import { useState } from "react";
import LoadingSpinner from "../../../components/Loader";
import FileDropzone from "../../../components/ui/FileDropzone";
import { PdfExam } from "../../../pages/platform/components/PdfExam";

interface FileDropzoneBoxProps {
    title: string;
    description?: string;
    files?: PdfExam[];
    isLoading?: boolean;
    error?: string | null;
    uploadedFile?: File;
    isUploading?: boolean;
    onFileSelect: (file: File) => void;
    onFileRemove: () => void;
    onUpload: () => void;
    uploadButtonText?: string;
    selectButtonText?: string;
    placeholder?: string;
    showFiles?: boolean;
}

const FileDropzoneBox: React.FC<FileDropzoneBoxProps> = ({
    title,
    description,
    files,
    isLoading = false,
    error,
    uploadedFile,
    isUploading = false,
    onFileSelect,
    onFileRemove,
    onUpload,
    uploadButtonText = "Carica file",
    selectButtonText = "Scegli file",
    placeholder = "Trascina qui un file PDF o immagine, oppure clicca per selezionarlo",
    showFiles = true
}) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const handleToggle = () => {
        setIsExpanded(prev => !prev);
    };

    const handleDownload = (exam: PdfExam) => {
        // Direct download using the file URL
        window.open(exam.url, '_blank');
    };

    const handleDelete = (exam: PdfExam) => {
        alert("La funzionalità di eliminazione sarà implementata presto.");
    };

    return (
        <div className="space-y-[25px] border rounded-xl p-2 border border-solid border-holifya-grey-shadow shadow-platform-new">
            <div className="font-bold text-[16px] leading-[33px] flex justify-between items-center">
                <span>{title}</span>
                <button
                    onClick={handleToggle}
                    className="text-blue-500 text-sm hover:text-blue-700"
                >
                    {isExpanded ? "Mostra meno" : "Mostra di più"}
                </button>
            </div>
            
            {isExpanded && (
                <div className="space-y-[20px]">
                    {description && (
                        <p className="text-sm text-gray-600">{description}</p>
                    )}
                    
                    {/* File Dropzone */}
                    <div className="w-full max-w-2xl">
                        <FileDropzone
                            onFileSelect={onFileSelect}
                            onFileRemove={onFileRemove}
                            onUpload={onUpload}
                            selectedFile={uploadedFile}
                            uploadButtonText={uploadButtonText}
                            selectButtonText={selectButtonText}
                            placeholder={placeholder}
                            isLoading={isUploading}
                        />
                    </div>
                    
                    {/* Error message */}
                    {error && (
                        <div className="text-red-500 text-sm p-2 bg-red-50 border border-red-200 rounded">
                            Errore: {error}
                        </div>
                    )}
                    
                    {/* Loading state */}
                    {isLoading && (
                        <div className="flex items-center space-x-2 text-blue-600">
                            <LoadingSpinner />
                            <span>Caricamento file...</span>
                        </div>
                    )}
                    
                    {/* Existing files - Simple list */}
                    {showFiles && files && files.length > 0 && (
                        <div>
                            <h4 className="font-medium text-sm mb-3">File caricati:</h4>
                            <div className="space-y-2">
                                {files.map((exam, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">
                                                {exam.filename || exam.url.split('/').pop() || 'File'}
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => handleDownload(exam)}
                                                className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                                                title="Scarica file"
                                            >
                                                <Download size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(exam)}
                                                className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                title="Elimina file"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Empty state */}
                    {showFiles && files && files.length === 0 && !isLoading && (
                        <div className="text-gray-500 text-center py-4">
                            <p>Nessun file caricato</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FileDropzoneBox; 